'use client';

import React, { useEffect, useRef, useState } from 'react';
import { gridMetrics } from './hero/HeroGrid';

type Rect = { left: number; top: number; width: number; height: number; fill: string };

/** Page background color — the default clearing fill. */
const PAGE_FILL = '#f8fafc';

/** Below this width the page grid is too tight to lay content on, so sections
 *  fall back to their natural layout and no clearing is drawn. Matches Tailwind `md`. */
const MOBILE_MAX = 768;

/**
 * Clears the page background grid over the **complete** cells covered by each
 * `[data-grid-block]` element inside `rootRef`. The clearing always snaps to whole
 * cells on both axes — content may sit mid-cell (e.g. start mid-column), but a grid
 * line is NEVER partially removed: if content reaches into a cell, the whole cell
 * clears. No stub/partial line segments, ever (see CLAUDE.md "Grid system").
 *
 * All four border lines of the cleared region are kept, so the surrounding grid
 * stays complete (margin cells and empty grid past a narrow block keep their
 * borders). Content's mid-column position is independent — it floats inside these
 * lines (the line at a block's edge still shows; the content just sits a bit in).
 *
 * Usage: mark the blocks that should sit in cleared cells with `data-grid-block`,
 * give their wrapper `position: relative`, and drop this in as its first child.
 * Desktop only (md+); on mobile it renders nothing.
 */
export default function GridClearing({
  rootRef,
}: {
  rootRef: React.RefObject<HTMLElement | null>;
}) {
  const layerRef = useRef<HTMLDivElement>(null);
  const [bands, setBands] = useState<Rect[]>([]);

  useEffect(() => {
    const compute = () => {
      const root = rootRef.current;
      const layerEl = layerRef.current;
      if (!root || !layerEl) return;

      const w = document.documentElement.clientWidth;
      if (w < MOBILE_MAX) {
        setBands([]);
        return;
      }

      const { cell, offsetX } = gridMetrics(w);
      const scrollY = window.scrollY;
      const layer = layerEl.getBoundingClientRect();
      const layerDocTop = layer.top + scrollY;

      const out: Rect[] = [];
      root.querySelectorAll<HTMLElement>('[data-grid-block]').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height) return;
        // Snap to WHOLE cells on both axes (vertical grid lines at offsetX + n*cell,
        // horizontal lines at document-y = n*cell). Content may sit mid-cell, but
        // the clearing always covers complete cells — a line is never partially
        // removed, so no stub segments are left behind.
        // EPS: row-aligned blocks land ON grid lines up to sub-pixel jitter — a
        // top measuring up to ~1px above its line must not floor into the
        // previous row (that would swallow the free row above the block).
        // Matches RowBand's and useBandRowAlign's own EPS (2.5): edges
        // engineered to land exactly on a row line (whole-row blocks, band
        // butts, the 1px anti-collapse `md:pt-px`/`md:pb-px` section paddings
        // — 2px combined where two flush sections meet) measure within ~2px
        // of it.
        //
        // The naive `Math.floor((v + EPS) / cell)` bakes the tolerance into a
        // FIXED direction (always "nudge up before flooring"). That's correct
        // for an edge sitting just BEFORE its line, but an edge sitting just
        // AFTER a line by chance (not engineered — genuinely mid-cell, only
        // coincidentally within EPS of the wrong neighboring line) gets the
        // same blind nudge and can snap a whole row outward in the wrong
        // direction. Nearest-line semantics fix this: absorb sub-EPS
        // misalignment toward whichever line is actually closest (either
        // direction), and only fall back to a plain floor/ceil — no epsilon —
        // once the edge is unambiguously mid-cell. That plain fallback must
        // still round OUTWARD (floor for top/left, ceil for bottom/right) so
        // content reaching into a cell always clears the whole cell (rule #1).
        const EPS = 2.5;
        const snapOuter = (v: number, dir: 'floor' | 'ceil') => {
          const nearest = Math.round(v / cell) * cell;
          if (Math.abs(v - nearest) <= EPS) return nearest;
          return (dir === 'floor' ? Math.floor(v / cell) : Math.ceil(v / cell)) * cell;
        };
        const left = offsetX + snapOuter(r.left - offsetX, 'floor');
        const right = offsetX + snapOuter(r.right - offsetX, 'ceil');
        const top = snapOuter(r.top + scrollY, 'floor');
        const bottom = snapOuter(r.bottom + scrollY, 'ceil');
        // Keep ALL FOUR border lines of the cleared region so the surrounding grid
        // stays complete (the margin cell beside the content keeps its border, and
        // empty grid past a narrow block keeps its border too). The body grid draws
        // each line at a cell's top-left edge, so inset the top-left by 1px and leave
        // the bottom/right edges on their lines — all four borders survive. Content's
        // mid-column position is independent: it floats inside these lines.
        out.push({
          left: Math.round(left - layer.left) + 1,
          top: Math.round(top - layerDocTop) + 1,
          width: Math.round(right - left) - 1,
          height: Math.round(bottom - top) - 1,
          // Blocks may opt into a non-page fill (e.g. a white "sheet", or the
          // surrounding RowBand color so a dark band shows at rounded corners).
          fill: el.dataset.gridFill || PAGE_FILL,
        });
      });
      setBands(out);
    };

    compute();
    // Re-measure once fonts settle (text metrics shift the blocks).
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(compute).catch(() => {});

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener('resize', schedule);
    // Row-snapping helpers (GridRowCenter) shift layout after mount; they emit
    // this event so every clearing re-measures at the settled positions.
    window.addEventListener('grid-relayout', schedule);
    const root = rootRef.current;
    const ro = root ? new ResizeObserver(schedule) : null;
    if (root && ro) ro.observe(root);

    return () => {
      window.removeEventListener('resize', schedule);
      window.removeEventListener('grid-relayout', schedule);
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [rootRef]);

  return (
    <div ref={layerRef} aria-hidden className="pointer-events-none absolute inset-0 z-0">
      {bands.map((b, i) => (
        <div
          key={i}
          className="absolute"
          style={{ left: b.left, top: b.top, width: b.width, height: b.height, background: b.fill }}
        />
      ))}
    </div>
  );
}
